import React, { useEffect, useMemo, useState } from 'react'
import { clipboard, fetchApiData } from '../fnc.js'

var moment = require('moment');

const dayPresets = [1, 3, 7, 30];

const defaultRange = (days) => ({
	from: moment().subtract(days - 1, 'days').format('YYYY-MM-DD'),
	to: moment().format('YYYY-MM-DD')
});

/**
 * Best-effort carrier guess from tracking number shape; opens the carrier's official tracking page.
 * Unknown shapes fall back to Parcels universal tracking.
 */
function carrierTrackingInfo(raw) {
	const trimmed = String(raw ?? '').trim();
	if (!trimmed) return null;
	const code = trimmed.replace(/\s+/g, '');
	const enc = encodeURIComponent(code);

	// UPS — standard 18-char format starting with 1Z
	if (/^1Z[0-9A-Z]{16}$/i.test(code)) {
		return { carrier: 'UPS', url: `https://www.ups.com/track?tracknum=${enc}` };
	}

	// DHL Express — common JJD-style IDs
	if (/^JJD/i.test(code)) {
		return { carrier: 'DHL', url: `https://www.dhl.com/en/express/tracking.html?AWB=${enc}` };
	}

	// FedEx — 12- or 15-digit numeric (very common)
	if (/^\d{12}$/.test(code) || /^\d{15}$/.test(code)) {
		return { carrier: 'FedEx', url: `https://www.fedex.com/fedextrack/?trknbr=${enc}` };
	}

	// FedEx — 34-digit US long form (e.g. SmartPost / consolidated; often starts with 96)
	if (/^\d{34}$/.test(code)) {
		return { carrier: 'FedEx', url: `https://www.fedex.com/fedextrack/?trknbr=${enc}` };
	}

	// USPS — IMpb / Informed Delivery style (20–22 digits)
	if (/^\d{20,22}$/.test(code)) {
		return { carrier: 'USPS', url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${enc}` };
	}

	// DHL — 10- or 11-digit numeric piece IDs (after other length rules)
	if (/^\d{10}$/.test(code) || /^\d{11}$/.test(code)) {
		return { carrier: 'DHL', url: `https://www.dhl.com/en/express/tracking.html?AWB=${enc}` };
	}

	return {
		carrier: 'Parcels',
		url: `https://parcelsapp.com/en/tracking/${enc}`,
		viaParcels: true,
	};
}

export default function NewPackageArrival(props) {
	if (!props.securityClearance(props)) return null;

	const initialRange = defaultRange(7);
	const [rows, setRows] = useState([]);
	const [users, setUsers] = useState([]);
	const [summary, setSummary] = useState({});
	const [username, setUsername] = useState('');
	const [from, setFrom] = useState(initialRange.from);
	const [to, setTo] = useState(initialRange.to);
	const [activePreset, setActivePreset] = useState(7);
	const [searchTerm, setSearchTerm] = useState('');

	useEffect(() => {
		loadRows();
	}, [username, from, to]);

	const loadRows = () => {
		if (from && to && moment(from).isAfter(moment(to))) {
			props.info({ error: 'The start date must be before the end date.' });
			return;
		}

		props.loading(true);
		fetchApiData('tools', 'new_package_arrival', {
			method: 'list',
			username: username || undefined,
			startAt: from || undefined,
			endAt: to || undefined,
			limit: 2000,
		}, null, (response) => {
			props.loading(false);

			if (!response || !response.success) {
				setRows([]);
				setUsers([]);
				setSummary({});
				props.info({ error: response && response.error ? response.error : 'Unable to load new package arrivals.' });
				return;
			}

			setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
			setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
			setSummary(response.summary || {});
		});
	}

	const applyPreset = (days) => {
		const range = defaultRange(days);
		setActivePreset(days);
		setFrom(range.from);
		setTo(range.to);
	}

	const clearDates = () => {
		setActivePreset('all');
		setFrom('');
		setTo('');
	}

	const visibleRows = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return rows;

		return rows.filter((row) => {
			return [row.id, row.username, row.barcode, row.date]
				.filter((value) => value !== undefined && value !== null)
				.some((value) => String(value).toLowerCase().includes(term));
		});
	}, [rows, searchTerm]);

	const groupedUsers = useMemo(() => {
		return [...users]
			.sort((left, right) => {
				if (parseInt(right.count) !== parseInt(left.count)) {
					return parseInt(right.count) - parseInt(left.count);
				}
				return String(left.username).localeCompare(String(right.username));
			})
			.slice(0, 6);
	}, [users]);

	return <div className="container-fluid py-3">
		<div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
			<div>
				<h2 className="mb-1">New Package Arrival</h2>
				<div className="text-muted">Scanned packages saved by the mobile arrival flow.</div>
			</div>
			<div className="badge badge-dark p-2" title="Tool version">v0.1.0</div>
		</div>

		<div className="row mb-4">
			<div className="col-lg-3 col-md-6 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="text-muted text-uppercase small">Filtered Scans</div>
						<div className="display-4 font-weight-bold mb-0">{summary.totalRows || 0}</div>
					</div>
				</div>
			</div>
			<div className="col-lg-3 col-md-6 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="text-muted text-uppercase small">Users In Scope</div>
						<div className="display-4 font-weight-bold mb-0">{summary.uniqueUsers || 0}</div>
					</div>
				</div>
			</div>
			<div className="col-lg-3 col-md-6 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="text-muted text-uppercase small">Visible After Search</div>
						<div className="display-4 font-weight-bold mb-0">{visibleRows.length}</div>
					</div>
				</div>
			</div>
			<div className="col-lg-3 col-md-6 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="text-muted text-uppercase small">Latest Scan</div>
						<div className="h5 font-weight-bold mb-1">{summary.newestDate ? moment(summary.newestDate).format('MMM D, YYYY') : 'No data'}</div>
						<div className="text-muted small">{summary.newestDate ? moment(summary.newestDate).format('h:mm:ss A') : ''}</div>
					</div>
				</div>
			</div>
		</div>

		<div className="card border-0 shadow-sm mb-4">
			<div className="card-body">
				<div className="row align-items-end">
					<div className="col-xl-3 col-lg-4 mb-3">
						<label className="font-weight-bold">Username</label>
						<select
							className="form-control"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
						>
							<option value="">All users</option>
							{users.map((user) => <option key={user.username} value={user.username}>{user.username} ({user.count})</option>)}
						</select>
					</div>
					<div className="col-xl-2 col-lg-4 col-md-6 mb-3">
						<label className="font-weight-bold">Date From</label>
						<input
							type="date"
							className="form-control"
							value={from}
							onChange={(e) => {
								setActivePreset(null);
								setFrom(e.target.value);
							}}
						/>
					</div>
					<div className="col-xl-2 col-lg-4 col-md-6 mb-3">
						<label className="font-weight-bold">Date To</label>
						<input
							type="date"
							className="form-control"
							value={to}
							onChange={(e) => {
								setActivePreset(null);
								setTo(e.target.value);
							}}
						/>
					</div>
					<div className="col-xl-3 col-lg-6 mb-3">
						<label className="font-weight-bold">Quick Ranges</label>
						<div className="d-flex flex-wrap">
							{dayPresets.map((days) => <button
								key={days}
								type="button"
								className={`btn mr-2 mb-2 ${activePreset === days ? 'btn-primary' : 'btn-outline-primary'}`}
								onClick={() => applyPreset(days)}
							>
								Last {days} day{days > 1 ? 's' : ''}
							</button>)}
							<button
								type="button"
								className={`btn mb-2 ${activePreset === 'all' ? 'btn-dark' : 'btn-outline-dark'}`}
								onClick={clearDates}
							>
								All time
							</button>
						</div>
					</div>
					<div className="col-xl-2 col-lg-6 mb-3">
						<label className="font-weight-bold">Search</label>
						<input
							type="text"
							className="form-control"
							placeholder="Barcode, user, id..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>

				<div className="border-top pt-3 mt-2 d-flex flex-wrap align-items-center justify-content-between">
					<div className="text-muted mb-2 mb-md-0">
						Showing {visibleRows.length} row(s)
						{summary.oldestDate ? ` from ${moment(summary.oldestDate).format('MMM D, YYYY')}` : ''}
						{summary.newestDate ? ` to ${moment(summary.newestDate).format('MMM D, YYYY')}` : ''}
					</div>
					<button type="button" className="btn btn-outline-secondary" onClick={loadRows}>Refresh</button>
				</div>
			</div>
		</div>

		<div className="row mb-4">
			<div className="col-lg-8 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="mb-0">Arrival History</h5>
							<span className="badge badge-light">Newest first</span>
						</div>
						<div className="table-responsive">
							<table className="table table-hover align-middle mb-0">
								<thead className="thead-light">
									<tr>
										<th>ID</th>
										<th>User</th>
										<th>Tracking</th>
										<th>Scanned At</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{visibleRows.length === 0 ? <tr>
										<td colSpan="5" className="text-center text-muted py-5">No package arrivals match the selected filters.</td>
									</tr> : visibleRows.map((row) => {
										const track = carrierTrackingInfo(row.barcode);
										return <tr key={row.id}>
										<td className="font-weight-bold">#{row.id}</td>
										<td>
											<span className="badge badge-info text-light px-3 py-2">{row.username}</span>
										</td>
										<td style={{ minWidth: '260px' }}>
											{track ? <div>
												<a
													href={track.url}
													target="_blank"
													rel="noopener noreferrer"
													className="font-weight-bold text-primary d-inline-block"
													title={track.viaParcels ? 'Open Parcels universal tracking' : `Open ${track.carrier} tracking`}
												>{row.barcode}</a>
												<div className="small text-muted mt-1">
													<span className="badge badge-secondary mr-1">{track.carrier}</span>
													{track.viaParcels
														? 'Opens Parcels (universal lookup) in a new tab.'
														: 'Opens carrier site in a new tab.'}
												</div>
											</div> : <div>
												<div className="font-weight-bold">{row.barcode}</div>
												<div className="small text-muted">Tap copy to reuse the code.</div>
											</div>}
										</td>
										<td>
											<div>{moment(row.date).format('YYYY-MM-DD HH:mm:ss')}</div>
											<div className="small text-muted">{moment(row.date).fromNow()}</div>
										</td>
										<td className="text-right">
											<button
												type="button"
												className="btn btn-sm btn-outline-secondary"
												onClick={() => clipboard(row.barcode, props)}
											>
												Copy
											</button>
										</td>
									</tr>;
									})}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<div className="col-lg-4 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<h5 className="mb-3">Top Users In Current Date Window</h5>
						{groupedUsers.length === 0 ? <div className="text-muted">No users found for the current filters.</div> : groupedUsers.map((user) => <div key={user.username} className="d-flex justify-content-between align-items-center border rounded px-3 py-2 mb-2">
							<div>
								<div className="font-weight-bold">{user.username}</div>
								<div className="small text-muted">Scans in selected date range</div>
							</div>
							<span className="badge badge-primary badge-pill px-3 py-2">{user.count}</span>
						</div>)}

						<div className="border-top mt-4 pt-3 text-muted small">
							The username list is date-aware. Selecting a user narrows the table without losing the date context.
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
}